import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { ItemStock } from 'views/Restaurant/Menu/Menu/types';
import { setReportedStock, updateMinimumStock } from 'crud/management_api/stock_management';
import './CurrentStock.scss';

interface Option {
  id: number;
  name: string;
}

interface CurrentStockProps {
  itemId: number | string;
  itemStocks: ItemStock[];
  setItemStocks: (stocks: ItemStock[]) => void;
  unidadesMedida?: Option[];
}

type EditingStockType = { storageUnitId: number; field: 'reported' | 'min' } | null;

const CurrentStock: React.FC<CurrentStockProps> = ({
  itemId,
  itemStocks,
  setItemStocks,
  unidadesMedida = []
}) => {
  // Helper to get unit name/abbreviation from unidad_medida_id
  const getUnitName = (unidadMedidaId: number): string => {
    const unit = unidadesMedida.find(u => u.id === unidadMedidaId);
    if (unit) {
      // The name format is "Name - Acronym", extract just the acronym if available
      const parts = unit.name.split(' - ');
      return parts.length > 1 ? parts[1] : parts[0];
    }
    return '';
  };
  const [editingStock, setEditingStock] = useState<EditingStockType>(null);
  const [tempReportedStock, setTempReportedStock] = useState<string>('');
  const [tempMinStock, setTempMinStock] = useState<string>('');
  const [showLossConfirm, setShowLossConfirm] = useState(false);
  const [pendingStockUpdate, setPendingStockUpdate] = useState<{stock: ItemStock; newValue: number} | null>(null);

  // Update reported stock
  const handleUpdateReportedStock = async (stock: ItemStock, newValue: number) => {
    const prevStocks = [...itemStocks];
    
    // Optimistic update
    const updatedStocks = itemStocks.map(s =>
      s.storage_unit_id === stock.storage_unit_id
        ? { ...s, reported_stock: newValue, system_stock: newValue, last_stock_update: new Date().toISOString() }
        : s
    );
    setItemStocks(updatedStocks);

    try {
      // API call to update reported stock using the ingredient_id from the stock object
      const payload = {
        ingredient_id: stock.ingredient_id,
        storage_unit_id: stock.storage_unit_id,
        reported_stock: newValue
      };
      await setReportedStock(payload);
      toast.success('Stock actualizado exitosamente');
    } catch (error) {
      setItemStocks(prevStocks);
      toast.error('Error al actualizar el stock');
      console.error(error);
    }
  };

  // Update minimum stock
  const handleUpdateMinStock = async (stock: ItemStock, newValue: number) => {
    const prevStocks = [...itemStocks];
    
    // Optimistic update
    const updatedStocks = itemStocks.map(s =>
      s.storage_unit_id === stock.storage_unit_id
        ? { ...s, min_quantity: newValue }
        : s
    );
    setItemStocks(updatedStocks);

    try {
      // API call to update minimum stock using the ingredient_id from the stock object
      await updateMinimumStock(stock.ingredient_id, stock.storage_unit_id, newValue);
      toast.success('Cantidad mínima actualizada exitosamente');
    } catch (error) {
      setItemStocks(prevStocks);
      toast.error('Error al actualizar la cantidad mínima');
      console.error(error);
    }
  };

  const handleSaveReportedStock = (stock: ItemStock) => {
    if (tempReportedStock === '') {
      toast.error('Ingrese un valor válido');
      return;
    }
    const newValue = Number(tempReportedStock);
    if (isNaN(newValue)) {
      toast.error('Ingrese un número válido');
      return;
    }

    // Check for potential loss
    if (newValue < stock.system_stock) {
      setPendingStockUpdate({ stock, newValue });
      setShowLossConfirm(true);
      return;
    }

    handleUpdateReportedStock(stock, newValue);
    setEditingStock(null);
  };

  const handleSaveMinStock = (stock: ItemStock) => {
    if (tempMinStock === '') {
      toast.error('Ingrese un valor válido');
      return;
    }
    const newValue = Number(tempMinStock);
    if (isNaN(newValue)) {
      toast.error('Ingrese un número válido');
      return;
    }

    handleUpdateMinStock(stock, newValue);
    setEditingStock(null);
  };

  const confirmLossAndUpdate = () => {
    if (!pendingStockUpdate) return;
    const { stock, newValue } = pendingStockUpdate;
    handleUpdateReportedStock(stock, newValue);
    setShowLossConfirm(false);
    setPendingStockUpdate(null);
    setEditingStock(null);
  };

  const getStockStatus = (stock: ItemStock): { className: string; label: string } | null => {
    if (stock.min_quantity === null || stock.min_quantity === undefined) return null;
    if (stock.reported_stock < stock.min_quantity) return { className: 'status-low', label: 'BAJO' };
    if (stock.reported_stock <= stock.min_quantity * 1.05) return { className: 'status-optimal', label: 'Local' };
    return { className: 'status-excess', label: 'EXCEDENTE' };
  };

  return (
    <div className="current-stock-section">
      <div className="info-section-header">
        <div className="header-left">
          <FontAwesomeIcon icon={faBoxes} className="info-icon" />
          <h5>Inventario por Almacén</h5>
        </div>
      </div>

      <div className="stock-grid">
        {itemStocks.map((stock, index) => {
          const isEditing = editingStock?.storageUnitId === stock.storage_unit_id;
          const editingField = isEditing && editingStock ? editingStock.field : null;
          const status = getStockStatus(stock);

          const unitName = getUnitName(stock.unidad_medida_id);

          return (
            <div key={index} className="stock-card editable">
              <div className="stock-header">
                <div className="storage-info">
                  <span className="storage-name">{stock.storage_unit.name}</span>
                  <div className="storage-badges">
                    <span className={`storage-type ${stock.storage_unit.is_global ? 'global' : 'local'}`}>
                      {stock.storage_unit.is_global ? 'Global' : 'Local'}
                    </span>
                    {status && (
                      <span className={`stock-status-badge ${status.className}`}>
                        {status.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Horizontal Stock Fields */}
              <div className="stock-fields-row">
                {/* System Stock - Read Only */}
                <div className="stock-field">
                  <Form.Label>
                    Sistema
                    {unitName && <span className="unit-suffix">({unitName})</span>}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={stock.system_stock}
                    disabled
                    readOnly
                  />
                </div>

                {/* Reported Stock - Editable */}
                <div className="stock-field">
                  <Form.Label>
                    Reportado
                    {unitName && <span className="unit-suffix">({unitName})</span>}
                  </Form.Label>
                  {isEditing && editingField === 'reported' ? (
                    <div className="editable-field-group">
                      <Form.Control
                        type="number"
                        min="0"
                        value={tempReportedStock}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (!value.includes('-') && !isNaN(Number(value)))) {
                            setTempReportedStock(value);
                          }
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveReportedStock(stock);
                          else if (e.key === 'Escape') setEditingStock(null);
                          else if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <div className="field-actions">
                        <Button
                          className="stock-action-btn stock-btn-cancel"
                          onClick={() => setEditingStock(null)}
                        >
                          <FaTimes />
                          <span>Cancelar</span>
                        </Button>
                        <Button
                          className="stock-action-btn stock-btn-confirm"
                          onClick={() => handleSaveReportedStock(stock)}
                        >
                          <FaCheck />
                          <span>Confirmar</span>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Form.Control
                      type="number"
                      value={stock.reported_stock}
                      onClick={() => {
                        setEditingStock({ storageUnitId: stock.storage_unit_id, field: 'reported' });
                        setTempReportedStock(stock.reported_stock.toString());
                      }}
                      className="editable-input"
                      readOnly
                    />
                  )}
                </div>

                {/* Minimum Quantity - Editable */}
                <div className="stock-field">
                  <Form.Label>
                    Cant. Mín
                    {unitName && <span className="unit-suffix">({unitName})</span>}
                  </Form.Label>
                  {isEditing && editingField === 'min' ? (
                    <div className="editable-field-group">
                      <Form.Control
                        type="number"
                        min="0"
                        value={tempMinStock}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (!value.includes('-') && !isNaN(Number(value)))) {
                            setTempMinStock(value);
                          }
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveMinStock(stock);
                          else if (e.key === 'Escape') setEditingStock(null);
                          else if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <div className="field-actions">
                        <Button
                          className="stock-action-btn stock-btn-cancel"
                          onClick={() => setEditingStock(null)}
                        >
                          <FaTimes />
                          <span>Cancelar</span>
                        </Button>
                        <Button
                          className="stock-action-btn stock-btn-confirm"
                          onClick={() => handleSaveMinStock(stock)}
                        >
                          <FaCheck />
                          <span>Confirmar</span>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Form.Control
                      type="number"
                      value={stock.min_quantity}
                      onClick={() => {
                        setEditingStock({ storageUnitId: stock.storage_unit_id, field: 'min' });
                        setTempMinStock(stock.min_quantity.toString());
                      }}
                      className="editable-input"
                      readOnly
                    />
                  )}
                </div>
              </div>
              
              <div className="stock-item">
                <span className="stock-label">Última Actualización:</span>
                <span className="stock-date">
                  {new Date(stock.last_stock_update).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              {(stock.is_following || stock.is_favorite) && (
                <div className="stock-badges">
                  {stock.is_following && <span className="badge following">Siguiendo</span>}
                  {stock.is_favorite && <span className="badge favorite">Favorito</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Loss Confirmation Modal */}
      {showLossConfirm && pendingStockUpdate && (
        <div className="loss-confirm-overlay" onClick={() => setShowLossConfirm(false)}>
          <div className="loss-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Confirmar actualización</h4>
              <button className="close-btn" onClick={() => setShowLossConfirm(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="loss-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <div className="warning-title">Se registrará una pérdida</div>
                  <div className="warning-details">
                    <div className="detail-row">
                      <span className="label">Stock en sistema:</span>
                      <span className="value">{pendingStockUpdate.stock.system_stock}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Nuevo stock reportado:</span>
                      <span className="value">{pendingStockUpdate.newValue}</span>
                    </div>
                    <div className="detail-row loss">
                      <span className="label">Pérdida:</span>
                      <span className="value">{pendingStockUpdate.stock.system_stock - pendingStockUpdate.newValue}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLossConfirm(false);
                  setPendingStockUpdate(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={confirmLossAndUpdate}
              >
                Confirmar actualización
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentStock;
